import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

const DevelopmentIndex = lazy(() => import("./pages/development"));

const AbsenceList = lazy(() => import("./pages/absence-list"));
const AbsenceLimitList = lazy(() => import("./pages/absence-limit-list"));
const AccountUserPage = lazy(() => import("./pages/account-user"));
const AccountUser2FAPage = lazy(() => import("./pages/account-user-2fa-conf"));
const AccountUserHistory = lazy(() => import("./pages/account-user-history"));
const AccountUserList = lazy(() => import("./pages/account-user-list"));
const AdditionalI18nList = lazy(() => import("./pages/additional-i18n-list"));
const Basket = lazy(() => import("./pages/basket"));
const Calendar = lazy(() => import("./pages/calendar"));
const CampaignOpportunityPage = lazy(
  () => import("./pages/campaign-opportunity")
);
const CampaignKanban = lazy(() => import("./pages/campaign-kanban"));
const CampaignOpportunityList = lazy(
  () => import("./pages/campaign-opportunity-list")
);
const CampaignOpportunityHistory = lazy(
  () => import("./pages/campaign-opportunity-history")
);
const AccountCompanyHistory = lazy(
  () => import("./pages/account-company-history")
);
const CompanyPage = lazy(() => import("./pages/company"));
const CompanyList = lazy(() => import("./pages/company-list"));
const Commission = lazy(() => import("./pages/commission"));
const CommissionHistory = lazy(() => import("./pages/commission-history"));
const CommissionList = lazy(() => import("./pages/commission-list"));
const CommissionCalendar = lazy(() => import("./pages/commission-calendar"));
const Desktop = lazy(() => import("./pages/desktop"));
const DiscountList = lazy(() => import("./pages/discount-list"));
const DiscountCodeList = lazy(() => import("./pages/discount-code-list"));
const DocumentInvoiceList = lazy(() => import("./pages/document-invoice-list"));
const DocumentInvoice = lazy(() => import("./pages/document-invoice"));
const DocumentOrderList = lazy(() => import("./pages/document-order-list"));
const DocumentOrder = lazy(() => import("./pages/document-order"));
const DocumentOrderHistory = lazy(
  () => import("./pages/document-order-history")
);
const DocumentOffer = lazy(() => import("./pages/document-offer"));
const DocumentOfferList = lazy(() => import("./pages/document-offer-list"));
const DocumentOfferQuestionList = lazy(
  () => import("./pages/document-offer-question-list")
);
const DocumentOfferQuestion = lazy(
  () => import("./pages/document-offer-question")
);
const DocumentOfferQuestionHistory = lazy(
  () => import("./pages/document-offer-question-history")
);
const EmailMessage = lazy(() => import("./pages/email-message"));
const FavoriteList = lazy(() => import("./pages/favorites-list"));
const FileRepository = lazy(() => import("./pages/file-repository"));
const Gantt = lazy(() => import("./pages/gantt"));
const GanttConfig = lazy(() => import("./pages/gantt-config"));
const HolidayList = lazy(() => import("./pages/holiday-list"));
const Kanban = lazy(() => import("./pages/kanban"));
const KanbanConfig = lazy(() => import("./pages/kanban-config"));
const Login = lazy(() => import("./pages/login"));
const PriceIndividualList = lazy(() => import("./pages/price-individual-list"));
const ServicedProduct = lazy(() => import("./pages/serviced-product"));
const ServicedProductList = lazy(() => import("./pages/serviced-product-list"));
const ServicedProductHistory = lazy(
  () => import("./pages/serviced-product-history")
);
const TaskHistory = lazy(() => import("./pages/task-history"));
const TaskList = lazy(() => import("./pages/task-list"));
const Timeline = lazy(() => import("./pages/timeline"));
const TargetConfig = lazy(() => import("./pages/target"));
const UserPage = lazy(() => import("./pages/user"));
const UserList = lazy(() => import("./pages/user-list"));
const UserUserHistory = lazy(() => import("./pages/user-history"));
const UserQualificationList = lazy(
  () => import("./pages/user-qualification-list")
);

function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        {process.env.NODE_ENV === "development" && (
          <Route path="js/react" element={<DevelopmentIndex />} />
        )}
        <Route path="/">
          <Route index element={<Desktop />} />
          <Route path="info" element={<Desktop />} />
          <Route path="info/home" element={<Desktop />} />
          <Route
            path="info/editAccountUserDesktop/user_profile_id/:id"
            element={<Desktop />}
          />
        </Route>
        <Route path="security">
          <Route path="login" element={<Login />} />
          <Route path="login/ref/:refUrl" element={<Login />} />
          <Route path="logout" element={<Login />} />
          <Route path="index/*" element={<Login />} />
        </Route>
        <Route path="commission">
          <Route index element={<Navigate to="list" />}></Route>
          <Route
            path="listForCompany/list_company_id/:listCompanyId"
            element={<CommissionList />}
          />
          <Route path="list/company_id/:id" element={<CommissionList />} />
          <Route path="list" element={<CommissionList />} />
          <Route path="show" element={<Commission />} />
          <Route path="show/commission_id/:id" element={<Commission />} />
          <Route
            path="showCommissionHistory/commission_id/:id"
            element={<CommissionHistory />}
          />
          <Route path="calendar" element={<CommissionCalendar />} />
          <Route path="timeline" element={<Timeline />} />
        </Route>
        <Route path="kanban">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<KanbanConfig />}>
            <Route path="page/:pageNumber" element={<KanbanConfig />} />
          </Route>
          <Route path="show/id/:id" element={<Kanban />} />
        </Route>
        <Route path="gantt">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<GanttConfig />}>
            <Route path="page/:pageNumber" element={<GanttConfig />} />
          </Route>
          <Route path="show/id/:id" element={<Gantt />} />
        </Route>
        <Route path="userCalendar" element={<Calendar />} />
        <Route path="emailMessage/mailFolder" element={<EmailMessage />} />
        <Route
          path="fileDirectory/fileRepository"
          element={<FileRepository />}
        />
        <Route path="servicedProduct">
          <Route index element={<Navigate to="list" />}></Route>
          <Route
            path="listForAll/company_id/:companyId"
            element={<ServicedProductList />}
          />
          <Route
            path="show/serviced_product_id/:id"
            element={<ServicedProduct />}
          />
          <Route
            path="edit/serviced_product_id/:id"
            element={<ServicedProduct />}
          />
          <Route path="listForAll" element={<ServicedProductList />} />
          <Route path="list" element={<ServicedProductList />} />
          <Route
            path="showServicedProductHistory/serviced_product_id/:id"
            element={<ServicedProductHistory />}
          />
        </Route>
        <Route path="target">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<TargetConfig />}>
            <Route path="page/:pageNumber" element={<TargetConfig />} />
          </Route>
        </Route>
        {/* Listy */}
        <Route path="task">
          <Route index element={<Navigate to="list" />} />
          <Route path="list" element={<TaskList />} />
          <Route
            path="listForCompany/task_list_company_id/:id/filter/true/task_category/:type"
            element={<TaskList />}
          />
          <Route path="showTaskHistory/task_id/:id" element={<TaskHistory />} />
        </Route>
        <Route path="campaign">
          <Route path="show/campaign_id/:id" element={<CampaignKanban />} />
        </Route>
        <Route path="campaignOpportunity">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<CampaignOpportunityList />} />
          <Route
            path="list/company_id/:id"
            element={<CampaignOpportunityList />}
          />
          <Route
            path="show/opportunity_id/:id"
            element={<CampaignOpportunityPage />}
          />
          <Route path="show" element={<CampaignOpportunityPage />} />
          <Route
            path="edit/opportunity_id/:id"
            element={<CampaignOpportunityPage />}
          />
          <Route
            path="showCampaignOpportunityHistory/opportunity_id/:id"
            element={<CampaignOpportunityHistory />}
          />
        </Route>
        <Route path="accountCompany">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<CompanyList />} />
          <Route path="show/company_id/:id" element={<CompanyPage />} />
          <Route path="show" element={<CompanyPage />} />
          <Route path="edit/company_id/:id" element={<CompanyPage />} />
          <Route
            path="showAccountCompanyLog/company_id/:id"
            element={<AccountCompanyHistory />}
          />
        </Route>
        <Route path="accountUser">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<AccountUserList />} />
          <Route path="show/user_id/:id" element={<AccountUserPage />} />
          <Route path="edit/user_id/:id" element={<AccountUserPage />} />
          <Route
            path="changePassword/user_id/:id"
            element={<AccountUserPage withTooltipChangePassword />}
          />
          <Route
            path="showAccountUserLog/account_user_id/:id"
            element={<AccountUserHistory />}
          />
        </Route>
        <Route path="userUser">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<UserList />} />
          <Route path="show/userid/:id" element={<UserPage />} />
          <Route path="show/userid/:id/deleted/true" element={<UserPage />} />
          <Route path="edit/userid/:id" element={<UserPage />} />
          <Route
            path="showUserUserLog/user_id/:id"
            element={<UserUserHistory />}
          />
        </Route>
        <Route path="documentOrder">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DocumentOrderList />}>
            {/* Dodatkowy fallback dla linkowania do listy ze starych widgetów */}
            <Route
              path="company_id/:listCompanyId"
              element={<DocumentOrderList />}
            />
          </Route>
          <Route
            path="/documentOrder/listForCompany/company_id/:listCompanyId"
            element={<DocumentOrderList />}
          />
          <Route
            path="/documentOrder/listForCompany"
            element={<DocumentOrderList />}
          />
          <Route
            path="/documentOrder/show/document_id/:id"
            element={<DocumentOrder initDocumentState="show" />}
          />
          <Route
            path="/documentOrder/listProcess/document_id/:id"
            element={<DocumentOrder initDocumentState="show" />}
          />
          <Route
            path="/documentOrder/createByPh/opportunity_id?/:opportunityId?/company_id?/:companyId?"
            element={<DocumentOrder initDocumentState="create" />}
          />
          <Route
            path="/documentOrder/createPhOrderFromOfferQuestion/company_id/:companyId/document_id/:id/from_offer/:fromOffer/positions_from_document/:positionsIds/delivery_place?/:deliveryPlace?/delivery_type?/:deliveryType?/payment_type?/:paymentType?"
            element={<DocumentOrder initDocumentState="create" />}
          />
          <Route
            path="/documentOrder/makeOrderFromOfferQuestion/document_id/:id"
            element={
              <DocumentOrder
                initDocumentState="create"
                makeOrderFromOfferQuestion={true}
              />
            }
          />
          <Route
            path="/documentOrder/createByPh/commission_id/:commissionId/company_id/:companyId"
            element={<DocumentOrder initDocumentState="create" />}
          />
          <Route
            path="/documentOrder/createPhOrderFromOrder/document_id/:referenceDocumentId/positions_from_document?/:positionsIds"
            element={<DocumentOrder initDocumentState="create" />}
          />
          <Route
            path="/documentOrder/createByPh/opportunity_id/:opportunityId/company_id/:companyId"
            element={<DocumentOrder initDocumentState="create" />}
          />
          <Route
            path="/documentOrder/editDocumentOrder/document_id/:id"
            element={<DocumentOrder initDocumentState="edit" />}
          />
          <Route
            path="/documentOrder/showDocumentOrderHistory/document_id/:id"
            element={<DocumentOrderHistory />}
          />
        </Route>
        <Route path="documentOffer">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DocumentOfferList />} />
          <Route
            path="/documentOffer/listForCompany/company_id/:listCompanyId"
            element={<DocumentOfferList />}
          />
          <Route
            path="/documentOffer/listProcess/document_id/:id"
            element={<DocumentOffer initDocumentState="show" />}
          />
          <Route
            path="/documentOffer/editDocumentOffer/document_id/:id"
            element={<DocumentOffer initDocumentState="edit" />}
          />
        </Route>
        <Route path="documentOfferQuestion">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DocumentOfferQuestionList />}>
            {/* Dodatkowy fallback dla linkowania do listy ze starych widgetów */}
            <Route
              path="company_id/:listCompanyId/document_subtype/:listDocumentSubtype"
              element={<DocumentOfferQuestionList />}
            />
          </Route>
          <Route
            path="/documentOfferQuestion/list/document_subtype?/:listDocumentSubtype"
            element={<DocumentOfferQuestionList />}
          />
          <Route
            path="/documentOfferQuestion/listForCompany/company_id/:listCompanyId/document_subtype/:listDocumentSubtype"
            element={<DocumentOfferQuestionList />}
          />
          <Route
            path="/documentOfferQuestion/createByPh/commission_id?/:commissionId?/opportunity_id?/:opportunityId?/document_offer_question_id?/:offerQuestionId?/company_id?/:companyId?/document_offer_question_subtype?/:documentSubtype?"
            element={<DocumentOfferQuestion initDocumentState="create" />}
          />
          <Route
            path="/documentOfferQuestion/listProcess/document_id/:id"
            element={<DocumentOfferQuestion initDocumentState="show" />}
          />
          <Route
            path="/documentOfferQuestion/show/document_id/:id"
            element={<DocumentOfferQuestion initDocumentState="show" />}
          />
          <Route
            path="/documentOfferQuestion/editDocument/document_id/:id"
            element={<DocumentOfferQuestion initDocumentState="edit" />}
          />
          <Route
            path="/documentOfferQuestion/createByPhFromDocument/from_document_id/:id/company_id/:companyId/document_offer_question_subtype/OH/positions_from_document/:positionsIds/with_offer_attributes?/:isDocumentAttributesCopied?/delivery_place?/:deliveryPlace?/delivery_type?/:deliveryType?/payment_type?/:paymentType?"
            element={<DocumentOfferQuestion initDocumentState="create" />}
          />
          <Route
            path="/documentOfferQuestion/showDocumentOfferQuestionHistory/document_id/:id"
            element={<DocumentOfferQuestionHistory />}
          />
        </Route>
        <Route path="documentInvoice">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DocumentInvoiceList />}>
            {/* Dodatkowy fallback dla linkowania do listy ze starych widgetów */}
            <Route
              path="company_id/:listCompanyId"
              element={<DocumentInvoiceList />}
            />
          </Route>
          <Route
            path="listPurchase"
            element={<DocumentInvoiceList documentType="2" />}
          />
          <Route
            path="listForCompany/document_type/:listDocumentType/company_id/:listCompanyId"
            element={<DocumentInvoiceList />}
          />
          <Route
            path="listForCompany/company_id/:listCompanyId"
            element={<DocumentInvoiceList />}
          />
          <Route path="listForCompany" element={<DocumentInvoiceList />} />
          <Route
            path="listUnaccounted/"
            element={<DocumentInvoiceList listUnaccounted={true} />}
          />
          <Route
            path="/documentInvoice/createByPh/company_id?/:companyId?"
            element={<DocumentInvoice initDocumentState="create" />}
          />
          <Route
            path="/documentInvoice/createByPh/document_type/:documentType?/company_id?/:companyId?"
            element={<DocumentInvoice initDocumentState="create" />}
          />
          <Route
            path="/documentInvoice/createByPh/document_type?/:documentType?/commission_id/:commissionId?/company_id?/:companyId?"
            element={<DocumentInvoice initDocumentState="create" />}
          />
          <Route
            path="/documentInvoice/createByPh/to_kz_id/:id?/is_kz/true"
            element={
              <DocumentInvoice
                initDocumentState="create"
                isAdvanceInvoice={true}
              />
            }
          />
          <Route
            path="/documentInvoice/createByPh/to_kz_id/:id?/is_kz/true/is_settled/false"
            element={
              <DocumentInvoice
                initDocumentState="create"
                isAdvanceInvoice={true}
              />
            }
          />
          <Route
            path="/documentInvoice/editDocument/document_id/:id"
            element={<DocumentInvoice initDocumentState="edit" />}
          />
          <Route
            path="/documentInvoice/createByPhCorrect/to_document_id/:id"
            element={
              <DocumentInvoice
                initDocumentState="create"
                isCorrectionDocument={true}
              />
            }
          />
        </Route>
        <Route path="documentInvoicePosition">
          <Route
            path="/documentInvoicePosition/list/document_id/:id"
            element={<DocumentInvoice initDocumentState="show" />}
          />
        </Route>
        <Route path="userUserAbsence">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<AbsenceList />} />
        </Route>
        <Route path="userUserAbsenceLimit">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<AbsenceLimitList />} />
        </Route>
        <Route path="holiday">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<HolidayList />} />
        </Route>
        <Route path="basketContent">
          <Route path="list/basket_id/:id" element={<Basket />} />
        </Route>
        <Route path="basket">
          <Route path="getDefault" element={<Basket />} />
          <Route
            path="/basket/makeOrder/basket_id/:basketId"
            element={<DocumentOrder initDocumentState="create" />}
          />
          <Route
            path="/basket/makeOffer/basket_id/:basketId"
            element={<DocumentOffer initDocumentState="create" />}
          />
        </Route>
        <Route path="discountCode">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DiscountCodeList />} />
          <Route path="list/company_id/:id" element={<DiscountCodeList />} />
        </Route>
        <Route path="discount">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<DiscountList />} />
          <Route path="list/company_id/:id" element={<DiscountList />} />
        </Route>
        <Route path="priceIndividual">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<PriceIndividualList />} />
          <Route path="list/company_id/:id" element={<PriceIndividualList />} />
        </Route>
        <Route path="additionalI18n">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<AdditionalI18nList />} />
        </Route>
        <Route path="favorite">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<FavoriteList />} />
        </Route>
        <Route path="settings" element={<AccountUser2FAPage />}></Route>
        <Route path="userUserQualification">
          <Route index element={<Navigate to="list" />}></Route>
          <Route path="list" element={<UserQualificationList />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
